import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import * as qs from 'qs';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL, API_KEY } from "../config/config";
import { getQueryParam } from "../utils/queryParams";
import { AuthContext } from "./authContext";

const SearchContext = createContext(null);

const SearchProvider = ({ children }) => {
	const { visitor } = useContext(AuthContext);
	const location = useLocation();

	const [searchQuery, setSearchQuery] = useState(null);
	const [threads, setThreads] = useState([]);
	const [threadsLoading, setThreadsLoading] = useState(false);
	const [threadsPage, setThreadsPage] = useState(1);
	const [hasMoreThreads, setHasMoreThreads] = useState(true);

	const fetchSearchThreads = useCallback(async (reset = false, query = null, page = 1) => {
		if (!visitor || !query) return;

		if (threadsLoading) return; // Loading

		if (searchQuery !== null) return; // Already loaded

		try {
			setThreadsLoading(true);
			setSearchQuery(query);

			let data = qs.stringify({ 'query': query });
			let config = {
				method: 'post',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/react-home/search?page=${reset ? 1 : page}`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id, // Public API, can use superuser ID,
					'Content-Type': 'application/x-www-form-urlencoded'
				},
				data: data,
				withCredentials: true
			};

			const response = await axios.request(config);
			let newThreads = response.data.threads;
			if (typeof newThreads === "object") {
				newThreads = Object.values(newThreads);
			}

			setThreads(prevThreads => reset ? newThreads : [...prevThreads, ...newThreads]);
			let pagination = response.data.pagination;

			setHasMoreThreads(pagination.current_page < pagination.last_page);
			setThreadsPage(reset ? 2 : prevPage => prevPage + 1);
			setThreadsLoading(false);
		} catch (error) {
			console.error('Failed to fetch threads:', error);
			setThreadsLoading(false);
			setSearchQuery(query);
		}
	}, [searchQuery, threadsLoading, visitor]);

	const loadMoreThreads = () => {
		if (!hasMoreThreads) return;

		fetchSearchThreads(false, searchQuery, threadsPage + 1);
	};

	useEffect(() => {
		const query = getQueryParam('q', location);
		if (query) {
			fetchSearchThreads(true, query, 1);
		} else {
			setThreads([]);
			setThreadsPage(1);
		}
	}, [location, searchQuery, fetchSearchThreads]);

	return (
		<SearchContext.Provider value={{
			searchQuery, setSearchQuery, fetchSearchThreads,
			threads, threadsLoading, hasMoreThreads, loadMoreThreads, setThreads, setThreadsPage,
		}}>
			{children}
		</SearchContext.Provider>
	);
};

export { SearchContext, SearchProvider };
